import { type ClassValue, clsx } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * The `nx-*` design-system scales, declared to tailwind-merge.
 *
 * tailwind-merge ships a conflict map for STOCK Tailwind class names only. It has no
 * access to the Tailwind config, so a theme namespace it has never been told about is
 * unresolvable — and it fails SILENTLY, in two different directions:
 *
 *   twMerge("rounded-nx-sm", "rounded-nx-md")            // both survive
 *   twMerge("text-nx-text-primary", "text-nx-caption")   // the COLOUR is dropped
 *
 * Both read as a plausible class list, which is why neither ever raised.
 *
 * ── the two failure modes ──────────────────────────────────────────────────────────
 *
 * 1. UNKNOWN GROUP. `rounded-nx-*` matched no group, so a component default and a
 *    caller override BOTH landed in the class list and STYLESHEET ORDER decided the
 *    winner. Tailwind emits these five rules alphabetically (full, lg, md, none, sm),
 *    so `rounded-nx-sm` beat every override that met it — a caller asking for
 *    `rounded-nx-full` got a small radius and no error.
 *
 * 2. WRONG GROUP. `text-nx-*` is overloaded across two orthogonal concerns: colour
 *    (`text-nx-text-*`, `text-nx-icon-*`, `text-nx-surface-*`) and typography
 *    (`text-nx-{display,heading,body,caption}`, which set font-size + line-height +
 *    font-weight). tailwind-merge classified ALL of them as `text-color` and kept only
 *    the last, so one of the two was simply GONE. `shadow-nx-*` had the same shape:
 *    the box-shadow scale was classified as `shadow-color`, so it neither replaced a
 *    stock `shadow-*` nor was replaced by one.
 *
 * ── how to extend this ─────────────────────────────────────────────────────────────
 *
 * Every list below mirrors a `--<namespace>-nx-*` block in
 * `@agent-nexus/ui/styles/theme.css` (plus `apps/frontend/tw/tailwind-v4-config.css`
 * for `--shadow-nx-{bubble-menu,card,card-hover,panel}`). Adding a token there and NOT here reintroduces
 * failure mode 1 for that token. `cn.util.test.ts` reads those stylesheets at test
 * time and fails when the two drift, so this is a ratchet rather than a convention.
 *
 * Colour namespaces (`bg-nx-*`, `border-nx-*`, `ring-nx-*`, `fill-nx-*`, …) need no
 * entry: tailwind-merge's colour groups accept any value, so they already merge. Only
 * a namespace whose values it cannot guess needs declaring — measured, not assumed.
 */
const NX_RADIUS = ["nx-none", "nx-sm", "nx-md", "nx-lg", "nx-full"] as const

/**
 * The typography scale. Registered as `font-size` — the same group as `text-sm` —
 * because that is exactly what these are: `--text-nx-*` theme entries carrying a
 * font-size with an attached line-height and weight. Sharing the stock group means
 * they inherit its calibrated conflicts (font-size supersedes a preceding `leading-*`)
 * instead of needing a hand-written set that would drift.
 */
const NX_FONT_SIZE = ["nx-display", "nx-heading", "nx-body", "nx-caption"] as const

/**
 * The box-shadow scale. Deliberately an explicit list rather than a `nx-*` wildcard:
 * `--color-nx-shadow-*` exists as well, so a class under `shadow-nx-shadow-…` is a
 * shadow COLOUR and must stay in `shadow-color`. A prefix rule would swallow it.
 */
const NX_BOX_SHADOW = [
  "nx",
  "nx-light",
  "nx-overlay",
  "nx-field",
  "nx-bubble-menu",
  "nx-card",
  "nx-card-hover",
  "nx-panel"
] as const

export const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: NX_FONT_SIZE }],
      shadow: [{ shadow: NX_BOX_SHADOW }],
      rounded: [{ rounded: NX_RADIUS }],
      "rounded-s": [{ "rounded-s": NX_RADIUS }],
      "rounded-e": [{ "rounded-e": NX_RADIUS }],
      "rounded-t": [{ "rounded-t": NX_RADIUS }],
      "rounded-r": [{ "rounded-r": NX_RADIUS }],
      "rounded-b": [{ "rounded-b": NX_RADIUS }],
      "rounded-l": [{ "rounded-l": NX_RADIUS }],
      "rounded-ss": [{ "rounded-ss": NX_RADIUS }],
      "rounded-se": [{ "rounded-se": NX_RADIUS }],
      "rounded-ee": [{ "rounded-ee": NX_RADIUS }],
      "rounded-es": [{ "rounded-es": NX_RADIUS }],
      "rounded-tl": [{ "rounded-tl": NX_RADIUS }],
      "rounded-tr": [{ "rounded-tr": NX_RADIUS }],
      "rounded-br": [{ "rounded-br": NX_RADIUS }],
      "rounded-bl": [{ "rounded-bl": NX_RADIUS }]
    }
  }
})

/**
 * Combines clsx and tailwind-merge for optimal class composition.
 * Handles conditional classes and resolves Tailwind conflicts, INCLUDING the `nx-*`
 * design-system scales — see the note above for what "including" had to be taught.
 *
 * @example
 * cn("px-4", "px-6") // => "px-6" (tailwind-merge resolves conflict)
 * cn("rounded-nx-sm", "rounded-nx-full") // => "rounded-nx-full" (the caller wins)
 * cn("text-nx-text-primary", "text-nx-caption") // => both — colour and size are orthogonal
 * cn("bg-red-500", condition && "bg-blue-500") // conditional
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
